/* CRMCompagniePartnerPage.jsx — Admin gestione mandati assicurativi */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Search, Pencil, Power, PowerOff, Building2, X, Save, Loader2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/crm/compagnie-partner`;

const TIPI = [
  ["compagnia_diretta", "Compagnia diretta"],
  ["lloyd_market", "Lloyd's Market"],
  ["broker_estero", "Intermediario estero"],
  ["riassicurazione", "Riassicurazione"],
  ["collaborazione_orizzontale", "Collaborazione orizzontale"],
];

const TIPO_BADGE_STYLE = {
  compagnia_diretta: "bg-emerald-100 text-emerald-800",
  lloyd_market: "bg-sky-100 text-sky-800",
  broker_estero: "bg-amber-100 text-amber-800",
  riassicurazione: "bg-slate-200 text-slate-800",
  collaborazione_orizzontale: "bg-violet-100 text-violet-800",
};
const PAESI_OPT = ["IT", "UK", "IE", "CH", "LI", "BE", "FR", "DE", "ES",
                    "LU", "MT", "NL", "AT", "PT"];
const RAMI_OPT = ["auto", "casa", "vita", "salute", "infortuni",
                   "tutela_legale", "danni", "danni_speciali",
                   "responsabilita_civile_pro", "rc_professionale"];
const PRODOTTI_OPT = ["auto", "casa", "vita", "salute", "infortuni",
                       "professioni", "aziende", "tutela_legale_famiglia",
                       "tutela_legale_professionale", "tutela_legale_aziendale",
                       "rc_professionale", "polizze_speciali", "viaggio"];

const EMPTY = {
  ragione_sociale: "", nome_commerciale: "", tipo: "compagnia_diretta",
  paese: "IT", rui_estero_numero: "", ivass_codice: "",
  rami_autorizzati: [], prodotti_distribuiti: [],
  mandato_attivo: true, data_inizio_mandato: "",
  data_fine_mandato: "",
  contatti: { nome_referente: "", email: "", telefono: "", pec: "" },
  url_riservata_intermediari: "", logo_url: "",
  ordine_visualizzazione: 100,
  visible_on_homepage: true, visible_in_quote_engine: true,
  note: "",
};

export default function CRMCompagniePartnerPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // {id?, ...}
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(API,
        { params: q ? { q } : {}, withCredentials: true });
      setItems(data.items || []);
    } catch (e) {
      toast.error("Errore caricamento compagnie");
    } finally { setLoading(false); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...editing };
      delete body.id; delete body.deleted_at;
      delete body.created_at; delete body.updated_at; delete body.seeded;
      if (editing.id) {
        await axios.put(`${API}/${editing.id}`, body, { withCredentials: true });
        toast.success("Compagnia aggiornata");
      } else {
        await axios.post(API, body, { withCredentials: true });
        toast.success("Compagnia creata");
      }
      setEditing(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const toggleActive = async (it) => {
    if (it.mandato_attivo) {
      if (!window.confirm(`Sospendere il mandato con ${it.ragione_sociale}?`)) return;
      try {
        await axios.delete(`${API}/${it.id}`, { withCredentials: true });
        toast.success("Mandato cessato");
        load();
      } catch { toast.error("Errore"); }
    } else {
      try {
        await axios.put(`${API}/${it.id}`,
          { ...it, mandato_attivo: true }, { withCredentials: true });
        toast.success("Mandato riattivato");
        load();
      } catch { toast.error("Errore"); }
    }
  };

  return (
    <div data-testid="compagnie-partner-page" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[#163321]" />
          <h2 className="text-xl font-bold text-[#163321]">
            Compagnie Partner / Mandati Assicurativi
          </h2>
        </div>
        <Button
          onClick={() => setEditing({ ...EMPTY })}
          className="bg-[#163321] hover:bg-[#0e1f15]"
          data-testid="compagnia-new-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Nuovo mandato
        </Button>
      </div>

      <div className="flex gap-2 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cerca ragione sociale, codice IVASS..."
            value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="compagnia-search"
          />
        </div>
        <span className="text-xs text-slate-500">{items.length} mandati</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`compagnia-card-${it.id}`}
              className={[
                "rounded-2xl border bg-white p-4 transition-all hover:shadow-md",
                it.mandato_attivo ? "border-emerald-200" : "border-slate-200 opacity-60",
              ].join(" ")}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-[#163321] text-sm leading-tight break-words">
                    {it.ragione_sociale}
                  </h3>
                  {it.nome_commerciale && (
                    <p className="text-xs text-slate-500 mt-0.5">
                      Brand: {it.nome_commerciale}
                    </p>
                  )}
                </div>
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => setEditing({ ...it })}
                    className="p-1.5 rounded-md hover:bg-slate-100"
                    data-testid={`compagnia-edit-${it.id}`}
                  >
                    <Pencil className="w-4 h-4 text-slate-600" />
                  </button>
                  <button
                    onClick={() => toggleActive(it)}
                    className="p-1.5 rounded-md hover:bg-slate-100"
                    data-testid={`compagnia-toggle-${it.id}`}
                  >
                    {it.mandato_attivo
                      ? <PowerOff className="w-4 h-4 text-rose-600" />
                      : <Power className="w-4 h-4 text-emerald-600" />}
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 mb-2">
                <Badge className={`text-xs ${TIPO_BADGE_STYLE[it.tipo] || ""}`}>
                  {it.tipo === "collaborazione_orizzontale" && "🤝 "}
                  {TIPI.find(([k]) => k === it.tipo)?.[1] || it.tipo}
                </Badge>
                <Badge variant="outline" className="text-xs">{it.paese}</Badge>
                {it.ivass_codice && (
                  <Badge variant="outline" className="text-xs">
                    IVASS {it.ivass_codice}
                  </Badge>
                )}
                {!it.mandato_attivo && (
                  <Badge className="bg-rose-100 text-rose-700 text-xs">cessato</Badge>
                )}
              </div>
              <p className="text-xs text-slate-600">
                <b>Prodotti:</b> {(it.prodotti_distribuiti || []).join(", ") || "—"}
              </p>
              {it.note && (
                <p className="text-xs text-slate-500 mt-2 italic">{it.note}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Editor drawer */}
      {editing && (
        <CompagniaEditor
          value={editing} onChange={setEditing}
          onSave={save} onClose={() => setEditing(null)}
          saving={saving}
        />
      )}
    </div>
  );
}

function CompagniaEditor({ value, onChange, onSave, onClose, saving }) {
  const v = value;
  const u = (patch) => onChange({ ...v, ...patch });
  const uContatti = (patch) =>
    onChange({ ...v, contatti: { ...(v.contatti || {}), ...patch } });
  const toggleArr = (k, val) => {
    const arr = v[k] || [];
    onChange({ ...v, [k]: arr.includes(val)
      ? arr.filter((x) => x !== val) : [...arr, val] });
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 flex items-end md:items-center justify-center p-0 md:p-6"
      data-testid="compagnia-editor"
    >
      <div className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-y-auto p-6 md:p-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-[#163321]">
            {v.id ? "Modifica mandato" : "Nuovo mandato"}
          </h3>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-slate-100"
                   data-testid="compagnia-editor-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>Ragione sociale *</Label>
            <Input value={v.ragione_sociale}
                   onChange={(e) => u({ ragione_sociale: e.target.value })}
                   data-testid="compagnia-field-ragsoc" />
          </div>
          <div>
            <Label>Nome commerciale (brand)</Label>
            <Input value={v.nome_commerciale || ""}
                   onChange={(e) => u({ nome_commerciale: e.target.value })} />
          </div>
          <div>
            <Label>Tipo *</Label>
            <Select value={v.tipo} onValueChange={(x) => u({ tipo: x })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPI.map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Paese ISO2 *</Label>
            <Select value={v.paese} onValueChange={(x) => u({ paese: x })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAESI_OPT.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Codice IVASS</Label>
            <Input value={v.ivass_codice || ""}
                   onChange={(e) => u({ ivass_codice: e.target.value })} />
          </div>
          <div>
            <Label>RUI estero (numero)</Label>
            <Input value={v.rui_estero_numero || ""}
                   onChange={(e) => u({ rui_estero_numero: e.target.value })} />
          </div>
          <div>
            <Label>Inizio mandato</Label>
            <Input type="date" value={v.data_inizio_mandato || ""}
                   onChange={(e) => u({ data_inizio_mandato: e.target.value })} />
          </div>
          <div>
            <Label>Fine mandato (se cessato)</Label>
            <Input type="date" value={v.data_fine_mandato || ""}
                   onChange={(e) => u({ data_fine_mandato: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>Rami autorizzati</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {RAMI_OPT.map((r) => (
                <label key={r}
                       className={[
                         "px-3 py-1.5 rounded-full border-2 text-xs cursor-pointer",
                         (v.rami_autorizzati || []).includes(r)
                           ? "border-[#163321] bg-emerald-50 text-[#163321]"
                           : "border-slate-200 text-slate-600",
                       ].join(" ")}>
                  <input
                    type="checkbox" className="hidden"
                    checked={(v.rami_autorizzati || []).includes(r)}
                    onChange={() => toggleArr("rami_autorizzati", r)}
                  />
                  {r}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Prodotti distribuiti</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {PRODOTTI_OPT.map((p) => (
                <label key={p}
                       className={[
                         "px-3 py-1.5 rounded-full border-2 text-xs cursor-pointer",
                         (v.prodotti_distribuiti || []).includes(p)
                           ? "border-[#163321] bg-emerald-50 text-[#163321]"
                           : "border-slate-200 text-slate-600",
                       ].join(" ")}>
                  <input
                    type="checkbox" className="hidden"
                    checked={(v.prodotti_distribuiti || []).includes(p)}
                    onChange={() => toggleArr("prodotti_distribuiti", p)}
                  />
                  {p}
                </label>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>URL portale intermediari</Label>
            <Input value={v.url_riservata_intermediari || ""}
                   onChange={(e) => u({ url_riservata_intermediari: e.target.value })}
                   placeholder="https://..." />
          </div>
          <div className="md:col-span-2">
            <Label>Logo URL</Label>
            <Input value={v.logo_url || ""}
                   onChange={(e) => u({ logo_url: e.target.value })} />
          </div>
          <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
            <Input placeholder="Referente nome" value={v.contatti?.nome_referente || ""}
                   onChange={(e) => uContatti({ nome_referente: e.target.value })} />
            <Input placeholder="Email referente" value={v.contatti?.email || ""}
                   onChange={(e) => uContatti({ email: e.target.value })} />
            <Input placeholder="Telefono" value={v.contatti?.telefono || ""}
                   onChange={(e) => uContatti({ telefono: e.target.value })} />
            <Input placeholder="PEC" value={v.contatti?.pec || ""}
                   onChange={(e) => uContatti({ pec: e.target.value })} />
          </div>
          <div>
            <Label>Ordine visualizzazione</Label>
            <Input type="number" value={v.ordine_visualizzazione}
                   onChange={(e) => u({ ordine_visualizzazione: parseInt(e.target.value || "100", 10) })} />
          </div>
          <div className="flex flex-col gap-2 pt-6">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={v.mandato_attivo}
                        onCheckedChange={(c) => u({ mandato_attivo: !!c })} />
              Mandato attivo
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={v.visible_on_homepage}
                        onCheckedChange={(c) => u({ visible_on_homepage: !!c })} />
              Mostra in homepage
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={v.visible_in_quote_engine}
                        onCheckedChange={(c) => u({ visible_in_quote_engine: !!c })} />
              Disponibile in quote engine
            </label>
          </div>
          <div className="md:col-span-2">
            <Label>Note</Label>
            <Textarea rows={3} value={v.note || ""}
                       onChange={(e) => u({ note: e.target.value })} />
          </div>
        </div>

        <div className="flex gap-2 mt-6 sticky bottom-0 bg-white pt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annulla
          </Button>
          <Button onClick={onSave} disabled={saving || !v.ragione_sociale}
                  className="flex-1 bg-[#163321] hover:bg-[#0e1f15]"
                  data-testid="compagnia-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <><Save className="w-4 h-4 mr-1" /> Salva</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
